import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCIPs } from "../../network/cipAPI";
import AgTable from "../../components/AgTable";
import { useState } from "react";

import {
  storeCIPS,
  storeOperationalTime,
  storeWaterRecovery,
} from "../../Store/TemplateStore/template.action";
import errorIcon from "../../assets/error-icon.svg";
import PageLoading from "../../components/PageLoading";
import { HISTORIC_CIP_TABLE_COLUMNS } from "./historicCIPTable.config";
import { useTranslation } from "react-i18next";
import { UNITS } from "../../constants/globalStrings";
import { hasMetricValue } from "../../utils/Utils";
import "./index.scss";

const HistoricalCIPs = ({}) => {
  const { t } = useTranslation();
  const temperature = useSelector((state) => state.temperature);
  const mass = useSelector((state) => state.mass);
  const [rowData, setRowData] = useState([]);
  const gridRef = useRef(null);
  const [gridApi, setGridAPi] = useState(null);
  const [isFetchingCIPs, setFetchingCIP] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const pressure = useSelector((state) => state.pressure);
  const [allCipData, setAllCipData] = useState({
    cleanings: [],
    backwashes: [],
  });
  const [operationalTime, setOperationalTime] = useState(null);
  const [waterRecovery, setWaterRecovery] = useState(null);
  const colDefs = HISTORIC_CIP_TABLE_COLUMNS(t, temperature, mass);

  const dispatch = useDispatch();

  const onGridReady = useCallback((params) => {
    let gridApi = params.api;
    setGridAPi(gridApi);
  });

  const updateStoreCips = (data) => {
    dispatch(storeCIPS([...data]));
  };

  const updateStoreBackwashes = (data) => {
    dispatch({ type: "STORE_BACKWASHES", payload: [...data] });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchingCIP(true);
        setIsLoading(true);
        const data = await getCIPs();
        setFetchingCIP(false);
        if (data && data.status === 200 && data?.data && data?.data.cleanings) {
          // Separate cleanings and backwashes
          const { cleanings, backwashes, operational_time, water_recovery } =
            data.data;
          setAllCipData({ cleanings, backwashes });
          setRowData(cleanings); // Only show cleanings in the table
          setIsLoading(false);
          updateStoreCips(cleanings);
          updateStoreBackwashes(backwashes);

          if (operational_time != null) {
            setOperationalTime(operational_time);
            dispatch(storeOperationalTime(operational_time));
          }
          if (water_recovery != null) {
            setWaterRecovery(water_recovery);
            dispatch(storeWaterRecovery(water_recovery));
          }
        } else {
          setRowData([]);
          setAllCipData({ cleanings: [], backwashes: [] });
          updateStoreCips([]);
        }
      } catch (err) {
        setFetchingCIP(false);
      } finally {
        setIsDataLoaded(true);
        setIsLoading(false);
      }
    };
    if (!isFetchingCIPs) {
      fetchData();
    }
  }, []);

  // Prepare export data: cleanings, gap, backwashes
  const getExportData = () => {
    const { cleanings, backwashes } = allCipData;
    if (!cleanings.length && !backwashes.length) return [];
    // Add 5 empty rows as gap
    const emptyRows = [{}, {}, {}, {}, {}];
    // Add exportIndex to cleanings
    const cleaningsWithIndex = cleanings.map((c, i) => ({
      ...c,
      exportIndex: i + 1,
    }));
    // Add exportIndex to backwashes, starting after cleanings
    const backwashesWithIndex = backwashes.map((bw, i) => ({
      ...bw,
      exportIndex: cleanings.length + i + 1,
    }));
    return [...cleaningsWithIndex, ...emptyRows, ...backwashesWithIndex];
  };

  return (
    <div className="historic-container">
      {rowData && rowData.length > 0 && (
        <>
          {(hasMetricValue(operationalTime) || hasMetricValue(waterRecovery)) && (
            <div className="operational-metrics">
              {hasMetricValue(operationalTime) && (
                <div className="metric-group">
                  <div>
                    {t(
                      "HistoricalCIPEffectivnessTableStrings.operational_time"
                    )}
                  </div>
                  <div>
                    {parseFloat(operationalTime).toFixed(1)} {UNITS.HOURS}
                  </div>
                </div>
              )}
              {hasMetricValue(waterRecovery) && (
                <div className="metric-group">
                  <div>
                    {t("HistoricalCIPEffectivnessTableStrings.water_recovery")}
                  </div>
                  <div>
                    {parseFloat(waterRecovery).toFixed(2)}
                    {UNITS.PERCENTAGE}
                  </div>
                </div>
              )}
            </div>
          )}
          {isLoading ? (
            <PageLoading customHeight={"215px"} />
          ) : (
            <AgTable
              gridRef={gridRef}
              rowData={rowData}
              columnDefs={colDefs}
              onGridReady={onGridReady}
              name={`${t("excelSheetNames.historicalCIPS")}.csv`}
              infoPopoverText={t(
                "IdentifiedCIPStrings.cleaningEffectivenessSummary"
              )}
              questionPopoverText={t(
                "IdentifiedCIPStrings.troubleshootingCIPsAndCEBs"
              ).replaceAll("<unit>", pressure || "bar/psig")}
              exportData={getExportData()}
            />
          )}
        </>
      )}
      {rowData && rowData.length === 0 && isDataLoaded && (
        <div className="errorText12 card-error">
          <img src={errorIcon}></img>
          {t("IdentifiedCIPStrings.noCip")}
        </div>
      )}
    </div>
  );
};

export default HistoricalCIPs;
